import React, { useState, useEffect, useRef } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import {
	ResponsiveContainer,
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
} from 'recharts';
import axios from 'axios';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import CallSessionList from '../components/CallSessionList';
import '../App.css';

const GRADE_COLORS = {
	A: '#5cb85c',
	B: '#8bc34a',
	C: '#c9913a',
	D: '#9b5a1a',
	E: '#8b3020',
	F: '#b03030',
};

const load_scores = async (user_id, token, set_scores, set_loading, set_error) => {
	set_loading(true);
	try {
		const headers = token ? { Authorization: 'Bearer ' + token } : {};
		const response = await axios.get(
			Constants.SERVER_URL + '/api/dashboard/scores?days=30',
			{ headers }
		);
		set_scores(response.data.scores || []);
	} catch (error) {
		set_error('Failed to load dashboard data.');
	} finally {
		set_loading(false);
	}
};

const format_date = (date_str) => {
	const d = new Date(date_str);
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const make_dot_renderer = () => {
	return (props) => {
		const { cx, cy, payload } = props;
		const color = GRADE_COLORS[payload.grade] || '#e8e8e8';
		return (
			<circle
				key={payload.session_id}
				cx={cx}
				cy={cy}
				r={6}
				fill={color}
				stroke="#242424"
				strokeWidth={2}
				className="dashboard-chart-dot"
			/>
		);
	};
};

const render_tooltip = ({ active, payload }) => {
	if(!active || !payload || !payload.length) {
		return null;
	}
	const data = payload[0].payload;
	return (
		<div className="dashboard-tooltip">
			<span className={'dashboard-tooltip-grade dashboard-tooltip-grade-' + data.grade.toLowerCase()}>
				{data.grade}
			</span>
			<span className="dashboard-tooltip-pct">{data.percentage}%</span>
			<span className="dashboard-tooltip-name">{data.session_name}</span>
		</div>
	);
};

function DashboardChart({ scores, on_point_click }) {
	const active_session_ref = useRef(null);
	const chart_margin = window.innerWidth <= 768
		? { top: 10, right: 10, left: 0, bottom: 10 }
		: { top: 10, right: 50, left: 0, bottom: 10 };
	const yaxis_width = window.innerWidth <= 768 ? 38 : 60;

	const data = scores.map(score => ({
		...score,
		date_label: format_date(score.date),
	}));

	const handle_mouse_move = (chart_data) => {
		if(chart_data && chart_data.activePayload && chart_data.activePayload.length) {
			active_session_ref.current = chart_data.activePayload[0].payload.session_id;
		} else {
			active_session_ref.current = null;
		}
	};

	const handle_click = () => {
		if(active_session_ref.current) {
			on_point_click(active_session_ref.current);
		}
	};

	return (
		<ResponsiveContainer width="100%" height={340}>
			<LineChart
				data={data}
				margin={chart_margin}
				onMouseMove={handle_mouse_move}
				onClick={()=>{}}>
				<CartesianGrid stroke="#444" />
				<XAxis
					dataKey="date_label"
					stroke="#555"
					tick={{ fill: '#aaa', fontSize: 12 }}
				/>
				<YAxis
					domain={[0, 100]}
					stroke="#555"
					tick={{ fill: '#aaa', fontSize: 12 }}
					tickFormatter={v => v + '%'}
					width={yaxis_width}
				/>
				<Tooltip content={render_tooltip} cursor={{ stroke: '#555', strokeWidth: 1 }} />
				<Line
					type="monotone"
					dataKey="percentage"
					stroke="#2060a0"
					strokeWidth={2}
					dot={make_dot_renderer()}
					activeDot={{ r: 8, fill: '#0072e7', stroke: '#ffffff', strokeWidth: 1 }}
				/>
			</LineChart>
		</ResponsiveContainer>
	);
}

const render_empty = () => {
	return <p className="dashboard-empty">No scored sessions in the last 30 days.</p>;	
};

function DashboardView() {
	const { token } = useAuth();
	const user_id = localStorage.getItem(Constants.USER_ID);

	const [scores, set_scores] = useState([]);
	const [loading, set_loading] = useState(false);
	const [error, set_error] = useState('');
	const [highlighted_session_id, set_highlighted_session_id] = useState(null);

	useEffect(() => {
		if(user_id) {
			load_scores(user_id, token, set_scores, set_loading, set_error);
		}
	}, [token]);

	return (
		<main className="dashboard-main">
			<div className="dashboard-header">
				<h4 className="dashboard-title">Progress</h4>
				<span className="dashboard-range">Last 30 days</span>
			</div>
			<p className="dashboard-sub-title-text">Track your call skills progression over time</p>
			{error ? <Alert variant="danger">{error}</Alert> : null}
			<div className="dashboard-chart-card">
				{loading ? (
					<div className="dashboard-loading">
						<Spinner animation="border" />
					</div>
				) : scores.length === 0 ? (
					render_empty()
				) : (
					<DashboardChart scores={scores} on_point_click={set_highlighted_session_id} />
				)}
			</div>
			<CallSessionList highlighted_session_id={highlighted_session_id} />
		</main>
	);
}

export default DashboardView;
